/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { Row, Col, Card, CardBody, Table, Button, Input } from "reactstrap";
import Select from "react-select";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';

const DriverDashboard = () => {
    const dispatch = useDispatch();
    const [dashboardData, setDashboardData] = useState(null);
    const [drivers, setDrivers] = useState([]);

    // Filters
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const loadDashboard = async () => {
        dispatch(loadingTrue());
        let url = `/reports/drivers.php?1=1`; // Path-ah check panniko machan
        if (selectedDriver) url += `&driver_id=${selectedDriver.value}`;
        if (fromDate) url += `&from_date=${fromDate.format("YYYY-MM-DD")}`;
        if (toDate) url += `&to_date=${toDate.format("YYYY-MM-DD")}`;

        const res = await Handler({ method: "get", url });
        if (res?.success) {
            setDashboardData(res.data);
        }
        dispatch(loadingFalse());
    };

    const loadMasterData = async () => {
        const res = await Handler({ method: "get", url: "/driver/list.php" });
        if (res?.status || res?.success) {
            setDrivers(res.data?.map(d => ({ value: d.id, label: d.name })));
        }
    };

    useEffect(() => {
        loadMasterData();
        loadDashboard();
    }, []);

    if (!dashboardData) return null;

    const { summary, salary_trend_chart, work_distribution_chart, list } = dashboardData;

    // Pie Chart Data mapping
    const pieData = [
        // { name: 'Office Hours', value: parseFloat(work_distribution_chart?.office_hours || 0), color: '#0088FE' },
        { name: 'Office OT', value: parseFloat(work_distribution_chart?.office_ot || 0), color: '#00C49F' },
        { name: 'After Office OT', value: parseFloat(work_distribution_chart?.after_office_ot || 0), color: '#FFBB28' },
        { name: 'Holiday OT', value: parseFloat(work_distribution_chart?.holiday_ot || 0), color: '#FF8042' },
    ];

    return (
        <div className="dashboard-container p-4 bg-light min-vh-100">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="fw-bold text-dark">Work Logs</h3>
                <div className="d-flex gap-2">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div style={{ width: 200 }}>
                            <Select
                                options={drivers}
                                value={selectedDriver}
                                onChange={setSelectedDriver}
                                placeholder="Select Driver"
                                isClearable
                            />
                        </div>
                        <DatePicker
                            label="From"
                            value={fromDate}
                            onChange={setFromDate}
                            slotProps={{ textField: { size: 'small', style: { backgroundColor: 'white' } } }}
                        />
                        <DatePicker
                            label="To"
                            value={toDate}
                            onChange={setToDate}
                            slotProps={{ textField: { size: 'small', style: { backgroundColor: 'white' } } }}
                        />
                        <Button color="primary" onClick={loadDashboard}>Generate Report</Button>
                    </LocalizationProvider>
                </div>
            </div>

            {/* ⭐ Summary Cards */}
            <Row className="mb-4 g-3">
                {[
                    { label: "Total Trips", value: summary?.total_trips, icon: "bi-truck", color: "primary" },
                    { label: "Total Salary", value: `$${parseFloat(summary?.total_salary || 0).toFixed(2)}`, icon: "bi-currency-dollar", color: "success" },
                    { label: "Office OT Hours", value: `${summary?.total_office_ot_hours}h`, icon: "bi-clock-history", color: "info" },
                    { label: "After Office OT", value: `${summary?.total_after_office_ot_hours}h`, icon: "bi-moon-stars", color: "warning" },
                    { label: "Holiday OT", value: `${summary?.total_holiday_ot_hours}h`, icon: "bi-calendar-event", color: "danger" }
                ].map((item, idx) => (
                    <Col key={idx} md={2} className="flex-grow-1">
                        <Card className="border-0 shadow-sm rounded-4">
                            <CardBody className="p-3 text-center">
                                <div className={`h4 text-${item.color} mb-2`}><i className={`bi ${item.icon}`}></i></div>
                                <div className="text-muted small uppercase fw-bold" style={{ fontSize: '10px' }}>{item.label}</div>
                                <h4 className="fw-bold mb-0">{item.value || 0}</h4>
                            </CardBody>
                        </Card>
                    </Col>
                ))}
            </Row>

            {/* ⭐ Charts Section */}
            <Row className="mb-4 g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <CardBody>
                            <h6 className="fw-bold mb-4">Driver Salary Earning Trend</h6>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <LineChart data={salary_trend_chart}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="date" fontSize={10} tickFormatter={(str) => dayjs(str).format("DD MMM")} />
                                        <YAxis fontSize={10} />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="salary" stroke="#0d6efd" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <CardBody>
                            <h6 className="fw-bold mb-4">Work Hours Distribution</h6>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie data={pieData} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                            {pieData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} />)}
                                        </Pie>
                                        <Tooltip />
                                        <Legend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ⭐ Detailed Table Section */}
            <Card className="border-0 shadow-sm rounded-4">
                <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h6 className="fw-bold mb-0">Recent Job Logs</h6>
                        <Button color="light" size="sm" outline><i className="bi bi-download me-1"></i> Export CSV</Button>
                    </div>
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="table-light text-muted small uppercase">
                                <tr>
                                    <th>Date</th>
                                    <th>Lorry Type</th>
                                    <th>Route (Pickup - Delivery)</th>
                                    <th className="text-center">Hours (N/A)</th>
                                    <th className="text-center">OT (N/A/H)</th>
                                    <th className="text-end">Salary</th>
                                </tr>
                            </thead>
                            <tbody className="small">
                                {list?.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="fw-bold">{dayjs(row.job_date).format("DD-MM-YYYY")}</td>
                                        <td><span className="badge bg-light text-dark border">{row.lorry_type}</span></td>
                                        <td>
                                            <div className="fw-bold text-primary">{row.pickup_location}</div>
                                            <div className="text-muted extra-small">to {row.delivery_location}</div>
                                        </td>
                                        <td className="text-center">{row.office_hrs} / {row.after_office_hrs}</td>
                                        <td className="text-center fw-bold">
                                            <span className="text-primary">{row.office_hrs_ot}</span> |
                                            <span className="text-warning"> {row.after_office_hrs_ot}</span> |
                                            <span className="text-danger"> {row.holiday_ot}</span>
                                        </td>
                                        <td className="text-end fw-bold text-success">${parseFloat(row.salary_amount).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};

export default DriverDashboard;